/**
 * Byte progress across the match's loads (Phase 8 loading screen). Each item starts with an
 * expected size so the bar moves from the first byte; the server's own total replaces it as
 * soon as a loader reports one. Pure bookkeeping: the loaders call the returned callbacks.
 */
export interface ProgressEventLike {
  loaded: number;
  total: number;
}

interface Item {
  loaded: number;
  total: number;
  expected: number;
  done: boolean;
}

export class LoadProgress {
  private readonly items = new Map<string, Item>();

  constructor(private readonly onChange: ((fraction: number) => void) | null = null) {}

  /** Registers an item; the returned callback fits three's `loadAsync(url, onProgress)`. */
  track(key: string, expectedBytes: number): (event: ProgressEventLike) => void {
    const item: Item = { loaded: 0, total: 0, expected: Math.max(1, expectedBytes), done: false };
    this.items.set(key, item);
    this.emit();
    return (event) => {
      if (item.done) return;
      item.loaded = Math.max(item.loaded, event.loaded);
      if (event.total > 0) item.total = event.total;
      this.emit();
    };
  }

  /** Marks an item complete whatever its byte counts said. */
  finish(key: string): void {
    const item = this.items.get(key);
    if (!item || item.done) return;
    item.done = true;
    this.emit();
  }

  /** 0..1 over every tracked item, weighted by bytes. */
  fraction(): number {
    let loaded = 0;
    let total = 0;
    for (const item of this.items.values()) {
      const size = item.total > 0 ? item.total : item.expected;
      total += size;
      loaded += item.done ? size : Math.min(size, item.loaded);
    }
    return total > 0 ? loaded / total : 0;
  }

  private emit(): void {
    this.onChange?.(this.fraction());
  }
}
